export const SITE_URL = 'https://koostream.live';
export const SITE_NAME = 'Koostream';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/logo512.png`;
export const CONTACT_EMAIL = 'koostream7@gmail.com';
export const DMCA_EMAIL = 'koostream5@gmail.com';

export const DEFAULT_TITLE =
  'Koostream | Sports Fixtures, Schedules, Standings & Sports News';

export const DEFAULT_DESCRIPTION =
  'Koostream publishes sports fixtures, match schedules, league standings, and original sports commentary. We do not host copyrighted broadcasts. Official coverage belongs to licensed rights holders.';

export const DEFAULT_KEYWORDS =
  'sports fixtures, match schedules, premier league table, soccer news, NFL schedule, NBA fixtures, UFC events, cricket fixtures, F1 calendar, sports standings, Koostream';

export interface PageSeo {
  title: string;
  description: string;
  keywords?: string;
}

export const PAGE_SEO: Record<string, PageSeo> = {
  HOME: {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    keywords: DEFAULT_KEYWORDS,
  },
  SOCCER: {
    title: 'Soccer Fixtures & Match Schedules | Koostream',
    description:
      'Today’s soccer fixtures, kickoff times, and league schedules. Follow Premier League, La Liga, Serie A, Bundesliga, Ligue 1, and Champions League on Koostream.',
  },
  BOXING: {
    title: 'Boxing Fight Cards & Upcoming Bouts | Koostream',
    description:
      'Upcoming boxing fight cards, bout schedules, and event information. Koostream covers boxing news and fixtures. Licensed broadcasts belong to official rights holders.',
  },
  NFL: {
    title: 'NFL Schedule, Fixtures & Game Times | Koostream',
    description:
      'NFL regular-season and playoff schedules, kickoff times, and match previews. Check today’s NFL fixtures on Koostream.',
  },
  NBA: {
    title: 'NBA Schedule & Basketball Fixtures | Koostream',
    description:
      'NBA game schedules, tip-off times, and basketball fixtures. Follow regular-season and playoff matchups on Koostream.',
  },
  UFC: {
    title: 'UFC Event Schedule & Fight Cards | Koostream',
    description:
      'UFC event dates, fight cards, and MMA schedules. Koostream lists upcoming UFC fixtures and related sports news.',
  },
  MMA: {
    title: 'MMA Event Schedule & Fight Cards | Koostream',
    description:
      'Upcoming MMA events, fight cards, and schedules. Follow UFC and other mixed martial arts fixtures on Koostream.',
  },
  TENNIS: {
    title: 'Tennis Tournament Schedules & Fixtures | Koostream',
    description:
      'Tennis tournament schedules, match times, and fixture lists for major tours and events.',
  },
  RUGBY: {
    title: 'Rugby Fixtures & Match Schedules | Koostream',
    description:
      'Rugby union and rugby league fixtures, kickoff times, and upcoming match schedules.',
  },
  MOTOGP: {
    title: 'MotoGP Calendar & Race Schedules | Koostream',
    description:
      'MotoGP race calendar, session times, and motorcycle Grand Prix schedules.',
  },
  NHL: {
    title: 'NHL Schedule & Hockey Fixtures | Koostream',
    description:
      'NHL game schedules, puck-drop times, and ice hockey fixtures.',
  },
  MLB: {
    title: 'MLB Schedule & Baseball Fixtures | Koostream',
    description:
      'Major League Baseball schedules, first-pitch times, and upcoming games.',
  },
  F1: {
    title: 'Formula 1 Calendar & Race Schedules | Koostream',
    description:
      'Formula 1 race calendar, practice and qualifying times, and Grand Prix schedules.',
  },
  MLS: {
    title: 'MLS Schedule & Soccer Fixtures | Koostream',
    description:
      'Major League Soccer fixtures, kickoff times, and match schedules.',
  },
  CRICKET: {
    title: 'Cricket Fixtures & Match Schedules | Koostream',
    description:
      'Cricket match schedules for Tests, ODIs, T20s, and domestic competitions.',
  },
  TEAMS: {
    title: 'Team Fixtures & Upcoming Matches | Koostream',
    description:
      'Upcoming fixtures for popular sports teams. Check schedules, dates, and kickoff times.',
  },
  LEAGUES: {
    title: 'League Fixtures & Competition Schedules | Koostream',
    description:
      'Fixtures and schedules for major sports leagues and competitions.',
  },
  STANDINGS: {
    title: 'League Standings & Points Tables | Koostream',
    description:
      'Current points tables for the Premier League, La Liga, Bundesliga, Serie A, and other leagues.',
  },
  CONTACT: {
    title: 'Contact Koostream | Support, Feedback & Copyright Notices',
    description:
      'Contact Koostream for support, feedback, or DMCA copyright notices. We respond to valid takedown requests.',
  },
  ABOUT: {
    title: 'About Koostream | Sports Fixtures, News & Schedules',
    description:
      'Koostream is a sports information site for fixtures, schedules, standings, and original commentary. We do not host copyrighted broadcasts.',
  },
  BLOGS: {
    title: 'Sports News & Commentary | Koostream Blog',
    description:
      'Original sports commentary and analysis covering soccer, basketball, and major events. Editorial opinions from the Koostream team.',
  },
  PRIVACY: {
    title: 'Privacy Policy | Koostream',
    description:
      'How Koostream collects, uses, and protects personal information, including cookies and analytics.',
  },
  DMCA: {
    title: 'DMCA Copyright Policy | Koostream',
    description:
      'Koostream DMCA policy, designated agent details, and instructions for submitting a copyright takedown notice.',
  },
  TERMS: {
    title: 'Terms of Service | Koostream',
    description:
      'Terms of use for Koostream, including third-party content rules, copyright policy, and user responsibilities.',
  },
  NOT_FOUND: {
    title: 'Page Not Found | Koostream',
    description: 'The page you requested does not exist. Return to Koostream for sports fixtures and news.',
  },
};

export const absoluteUrl = (path = '/') => {
  if (!path) return SITE_URL;
  if (path.startsWith('http')) return path;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

export const organizationJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: DEFAULT_OG_IMAGE,
  email: CONTACT_EMAIL,
  sameAs: [SITE_URL],
  description: DEFAULT_DESCRIPTION,
});

export const websiteJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  description: DEFAULT_DESCRIPTION,
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    logo: {
      '@type': 'ImageObject',
      url: DEFAULT_OG_IMAGE,
    },
  },
});

export const breadcrumbJsonLd = (items: { name: string; path: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.path),
  })),
});

export const articleJsonLd = (article: {
  title: string;
  description: string;
  url: string;
  image: string;
  date: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.title,
  description: article.description,
  image: article.image,
  datePublished: article.date,
  dateModified: article.date,
  author: {
    '@type': 'Organization',
    name: `${SITE_NAME} Editorial`,
    url: SITE_URL,
  },
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    logo: {
      '@type': 'ImageObject',
      url: DEFAULT_OG_IMAGE,
    },
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': article.url,
  },
  copyrightHolder: {
    '@type': 'Organization',
    name: SITE_NAME,
  },
});

export const faqJsonLd = (faqs: { question: string; answer: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});
